import React, { useState, useRef, useEffect } from 'react';
import { Modal, Input, Spin } from 'antd';
import type { InputRef } from 'antd';
import SiteBrand from '../../../../core/components/SiteBrand';

interface TotpModalProps {
  visible: boolean;
  loading: boolean;
  onSubmit: (totp: string) => void;
  onCancel: () => void;
  username: string;
}

const TotpModal: React.FC<TotpModalProps> = ({
  visible,
  loading,
  onSubmit,
  onCancel,
  username
}) => {
  const [totp, setTotp] = useState('');
  const totpRef = useRef<InputRef>(null);

  useEffect(() => {
    if (visible) {
      // Reset TOTP when modal opens
      setTotp('');
      // Focus on TOTP input when modal opens
      setTimeout(() => totpRef.current?.focus(), 100);
    }
  }, [visible]);

  const handleTotpInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').substring(0, 6);
    setTotp(numericValue);
  };

  const handleSubmit = () => {
    if (totp.trim().length === 6) {
      onSubmit(totp.trim());
    }
  };

  const handleCancel = () => {
    setTotp('');
    onCancel();
  };

  return (
    <Modal
      
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={loading ? <Spin size="small" /> : 'Verify'}
      cancelText="Cancel"
      okButtonProps={{
        disabled: loading || totp.trim().length !== 6
      }}
      cancelButtonProps={{
        disabled: loading
      }}
      maskClosable={false}
      keyboard={false}
      width={400}
      centered
    >
      <div className="flex flex-col items-center text-center space-y-4 py-4">
        <div className="flex flex-col items-center space-y-3">
          <SiteBrand markSize={48} layout="stacked" markClassName="drop-shadow-sm" />
          <h2 className="text-lg font-semibold text-gray-800">
            Two-Factor Authentication
          </h2>
          <p className="text-sm text-gray-600 max-w-[280px]">
            Hi <strong>{username}</strong>, please enter your 6-digit authentication code
          </p>
        </div>

        <div className="flex flex-col items-center w-full space-y-3">
          <label htmlFor="totp"  className="text-sm font-medium text-gray-700 tracking-wide">
            Authenticator Code
          </label>
          <Input
            id="totp"
            name="totp"
            type="text"
            placeholder="000000"
            value={totp}
            onChange={handleTotpInput}
            ref={totpRef}
            maxLength={6}
             className="text-center text-2xl font-mono tracking-[0.5em] h-12 w-48 border-gray-300 rounded-lg focus:border-blue-500 focus:ring focus:ring-blue-100 transition"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && totp.trim().length === 6) {
                handleSubmit();
              }
              // Only allow numbers and control keys
              if (!/\d/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              // Remove non-digits
              const target = e.target as HTMLInputElement;
              target.value = target.value.replace(/\D/g, '');
            }}
          />
          <p className="text-xs text-gray-500">
            Open your authenticator app and enter the 6-digit code
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default TotpModal;
